import uniq from 'lodash/uniq';
import castArray from 'lodash/castArray';
import React from 'react';
import { useMutation as useMutationRQ, queryCache } from 'react-query';

import { useRedirectForbidden } from '@hooks';
import { notification } from '@atoms';

import * as utils from './utils';

const useMutation = (
  fn,
  {
    parseApiError = utils.parseApiError,
    invalidate,
    displayBulkErrors,
    onSuccessBulk,
    onErrorBulk,
    onSettledBulk,
    ...queryOptions
  } = {},
) => {
  const [error, setError] = React.useState(null);
  const redirectForbidden = useRedirectForbidden();

  const invalidateQueries = React.useCallback(() => {
    // Not sure if it's a good check. String and array will return true if something is set
    if (invalidate?.length > 0) {
      castArray(invalidate).forEach((key) => queryCache.invalidateQueries(key));
    }
  }, [invalidate]);

  const wrappedFn = React.useCallback(
    async (input) => {
      if (Array.isArray(input)) {
        const result = await Promise.allSettled(input.map(fn));

        const { good, bad } = await result.reduce(
          async (accP, { status, value, reason }) => {
            const acc = await accP;

            if (status === 'fulfilled') acc.good.push(value);
            if (status === 'rejected') acc.bad.push(await parseApiError({ error: reason }));
            return acc;
          },
          { good: [], bad: [] },
        );

        if (bad.length !== 0) {
          // eslint-disable-next-line no-unused-expressions
          await onErrorBulk?.(bad);

          if (displayBulkErrors) {
            notification.error({
              message: 'Errors occurred',
              description: uniq(bad.map(({ message }) => message)).map((e, idx, arr) => (
                <React.Fragment key={idx}>
                  {e}
                  {idx !== arr.length - 1 ? <br /> : null}
                </React.Fragment>
              )),
            });
          }
        }

        if (good.length !== 0) {
          // eslint-disable-next-line no-unused-expressions
          await onSuccessBulk?.(good);
        }

        // eslint-disable-next-line no-unused-expressions
        onSettledBulk?.(good, bad);

        return result;
      }

      // fix me: accepts only single argument on mutation invocation
      return fn(input);
    },
    [displayBulkErrors, fn, onErrorBulk, onSettledBulk, onSuccessBulk, parseApiError],
  );

  const [mutationFn, queryResult] = useMutationRQ(wrappedFn, {
    ...queryOptions,
    onMutate: (...args) => {
      setError(null);
      // eslint-disable-next-line no-unused-expressions
      return queryOptions.onMutate?.(...args);
    },
    onError: async (e, ...args) => {
      if (e.response?.status === 403) redirectForbidden();
      const parsedError = await parseApiError({ error: e });
      setError(parsedError);
      // eslint-disable-next-line no-unused-expressions
      return queryOptions.onError?.(parsedError, ...args);
    },
    onSuccess: (...args) => {
      setError(null);
      // eslint-disable-next-line no-unused-expressions
      return queryOptions.onSuccess?.(...args);
    },
    onSettled: (...args) => {
      invalidateQueries();
      // eslint-disable-next-line no-unused-expressions
      return queryOptions.onSettled?.(...args);
    },
  });

  const reset = React.useCallback(() => {
    queryResult.reset();
    setError(null);
  }, [queryResult]);

  return [
    mutationFn,
    {
      ...queryResult,
      reset,
      error,
    },
  ];
};

export default useMutation;
